import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Settings as SettingsIcon, DollarSign, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const Settings = () => {
  const [currencies, setCurrencies] = useState([]);
  const [currentCurrency, setCurrentCurrency] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const [currenciesRes, currentRes] = await Promise.all([
        axios.get(`${API}/settings/currencies`, getAuthHeader()),
        axios.get(`${API}/settings/currency`, getAuthHeader())
      ]);
      setCurrencies(currenciesRes.data);
      setCurrentCurrency(currentRes.data);
      setSelectedCurrency(currentRes.data);
    } catch (error) {
      toast.error('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedCurrency) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/settings/currency`, {
        currency_symbol: selectedCurrency.currency_symbol,
        currency_name: selectedCurrency.currency_name,
        currency_code: selectedCurrency.currency_code
      }, getAuthHeader());
      
      setCurrentCurrency(selectedCurrency);
      toast.success('Currency settings updated successfully');
      
      // Trigger a page reload to update all components with new currency
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleCurrencySelect = (currency) => {
    setSelectedCurrency({
      currency_symbol: currency.symbol,
      currency_name: currency.name,
      currency_code: currency.code
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-slate-500">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Settings
        </h1>
        <p className="text-lg text-slate-600 mt-2">Configure application settings</p>
      </div>

      {/* Currency Settings Card */}
      <div className="card p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 rounded-lg bg-emerald-100">
            <DollarSign className="w-6 h-6 text-emerald-600" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Currency Settings
            </h3>
            <p className="text-sm text-slate-500">Choose the currency symbol displayed throughout the application</p>
          </div>
        </div>

        {/* Current Currency */}
        <div className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
          <Label className="text-xs text-slate-500 uppercase tracking-wider">Current Currency</Label>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-3xl font-bold text-emerald-600">{currentCurrency?.currency_symbol}</span>
            <span className="text-lg text-slate-700">{currentCurrency?.currency_name}</span>
            <span className="text-sm text-slate-400">({currentCurrency?.currency_code})</span>
          </div>
        </div>

        {/* Currency Selection */}
        <div>
          <Label className="text-sm font-medium text-slate-700 mb-3 block">Select Currency</Label>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {currencies.map((currency) => {
              const isSelected = selectedCurrency?.currency_symbol === currency.symbol;
              const isCurrent = currentCurrency?.currency_symbol === currency.symbol;
              
              return (
                <button
                  key={currency.code}
                  data-testid={`currency-${currency.code}`}
                  onClick={() => handleCurrencySelect(currency)}
                  className={`relative p-4 rounded-lg border-2 transition-all text-left ${
                    isSelected
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-slate-200 hover:border-slate-300 bg-white'
                  }`}
                >
                  {isSelected && (
                    <div className="absolute top-2 right-2">
                      <Check className="w-5 h-5 text-emerald-600" />
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <span className={`text-2xl font-bold ${isSelected ? 'text-emerald-600' : 'text-slate-700'}`}>
                      {currency.symbol}
                    </span>
                    <div>
                      <p className={`font-medium ${isSelected ? 'text-emerald-700' : 'text-slate-900'}`}>
                        {currency.name}
                      </p>
                      <p className="text-xs text-slate-500">{currency.code}</p>
                    </div>
                  </div>
                  {isCurrent && (
                    <span className="absolute bottom-2 right-2 text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded">
                      Current
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Save Button */}
        <div className="mt-6 flex justify-end">
          <Button
            data-testid="save-currency-button"
            onClick={handleSave}
            disabled={saving || selectedCurrency?.currency_symbol === currentCurrency?.currency_symbol}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <div className="card p-4 bg-blue-50 border border-blue-200">
        <p className="text-sm text-blue-700">
          <strong>Note:</strong> Changing the currency will update the symbol displayed across all pages including Dashboard, Sales, Orders, Payments, and Reports. The page will reload after saving to apply the changes.
        </p>
      </div>
    </div>
  );
};

export default Settings;
